import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import type { Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import { getCompositionRestPoints } from '../html-design/lint/lintAsset';

import {
    formatNarration,
    formatNarrationWindow,
    narrationWindowAt,
    type TimedWord,
    timeNarrationWords,
} from '../html-design/sceneNarration';
import {
    captureSceneFrames,
    isHtmlGraphicScene,
    reviewProgressPoints,
    SCREENSHOT_MAX_EDGE,
} from '../rendering/sceneScreenshot';
import {
    type EnhancedToolResult,
    type ImageContent,
    Model,
    ReasoningEffort,
    type TextContent,
    type ToolExecutionContext,
} from '../types';

import { buildCheckFindingCounts } from './checkFindingAnalytics';
import { ConversationTool } from './ConversationTool';
import { ReportFindingsTool } from './ReportFindingsTool';
import { reviewSkippedResult } from './review-skipping';
import { describeMediaFraming } from './review-media-framing';
import {
    applicableChecks,
    type ReviewCheck,
    buildReviewInstruction,
    findingsSignature,
    formatReviewGate,
    type ReviewFinding,
} from './review-checks';
import { resolveSceneById, sceneDisplayId } from './scene-addressing';
import { getSceneTranscriptTokens } from './transcript-partition';
import { findHtmlDesignSkill } from '../html-design/skills/skillRegistry';
import { getSceneDescriptions } from '../video-state';

import {
    announceCreationStage,
    CreationStage,
    isReviewSkipped,
    setSceneReviewing,
} from 'js/src/data/agent-activity';
import { getSceneTechniques } from 'js/src/data/html-design';
import { isAIAvatarSceneGroup, isTalkingHeadSceneGroup } from 'js/src/luminary/luminary-selectors';
import { getVideoSizeAnalyticsProperties } from 'js/src/luminary/luminary-utils';

/** Narration cues print to one decimal, so a second read off one can sit this far past the end. */
const CUE_ROUNDING_TOLERANCE_S = 0.05;

/**
 * Consecutive blocking reviews a scene gets before it is released as accepted-with-residual. A
 * scene the reviewer won't pass has nothing else to release it, so this cap is what stops the
 * agent looping forever on it — whatever check it fails on each round.
 */
const MAX_REVIEW_ROUNDS = 3;

/**
 * Attaches the facts a finding needs to be acted on without another probe. Only an off-frame
 * finding gets any today: the media's position range is the one limit the agent cannot see and
 * that changes under it (AP-1149).
 */
function withFixContext(finding: ReviewFinding, scene: Scene): ReviewFinding {
    if (finding.check !== 'content-off-frame') {
        return finding;
    }
    const framing = describeMediaFraming(scene);
    return framing ? { ...finding, context: framing } : finding;
}

/**
 * Tracks blocking rounds and each scene's last finding set so a repeat can be called out and the
 * round cap enforced. Keyed `${conversationId}:${sceneId}`, matching `applyHtmlAndLint`'s own
 * repeat-detection key (htmlAssetToolHelpers.ts) — module-level because the tool is re-instantiated
 * per call, but the count has to survive across calls within the same conversation. A finding that
 * can't be attributed to a requested scene is keyed `${conversationId}:${UNATTRIBUTED_KEY}` instead,
 * so it is capped and released the same way rather than blocking forever.
 *
 * The findings are kept whole, not hashed: the next review is told which checks they came from.
 *
 * The round count is per scene, not per defect: it advances every time the scene blocks, whatever
 * check it blocks on, and only a clean review or the cap itself resets it. The reviewer re-judges
 * freshly rendered frames each round and is not consistent about which check it raises — one
 * round's `text-clipped` becomes the next round's `content-off-frame` on frames the agent barely
 * touched — so a count keyed on the defect identity was reset by that churn and let a scene block
 * indefinitely. The gate's "unchanged" wording still keys on the full finding, detail included,
 * being the stronger claim that an edit changed nothing.
 *
 * A narrowed review (`seconds` set) counts when it blocks but does not reset the count when it
 * is clean: it judges only the checks a single instant can show, so a clean result does not
 * prove the scene clean, while a block still cost the agent a fix round like any other.
 */
const blockingRoundsByScene = new Map<string, number>();
const lastFindingsByScene = new Map<string, ReviewFinding[]>();

/**
 * The video state a scene carried the last time a full-scan review passed it clean, keyed
 * `${conversationId}:${sceneId}` like the round maps. The agent is prompted to review scenes after
 * edits and is free to re-review a scene it has already passed, so an unchanged scene can be
 * rendered and re-judged more than once for no new information; a later full-scan review whose scene
 * still carries this exact state is skipped instead. An edit changes the state and drops the scene
 * back into review. A scene whose state can't be read is never recorded and so never skipped — the
 * safe default is to review.
 */
const passedSignatureByScene = new Map<string, string>();

/** Bucket for a finding whose scene_id resolves to none of the reviewed scenes. */
const UNATTRIBUTED_KEY = '__unattributed__';

/** Test-only: clears the module-level round-tracking state so specs don't leak into each other. */
export function resetReviewRoundsForTest(): void {
    blockingRoundsByScene.clear();
    lastFindingsByScene.clear();
    passedSignatureByScene.clear();
}

function allScenesAlreadyPassedResult(count: number): string {
    return (
        `All ${count} requested scene(s) already passed a full review and are unchanged since — ` +
        'not re-reviewed. Edit a scene to have it reviewed again.'
    );
}

function skippedSceneNote(count: number): string {
    return `(${count} other scene(s) already passed review and were skipped as unchanged.)`;
}

/**
 * The reviewer judges frames blind, so a scene showing the video's AI avatar or talking-head
 * speaker reads to it as a generic person in a staged office — a `media-mismatch` false positive
 * that then pushes the agent to bury the presenter under stock media. This note tells the reviewer
 * the presenter is intentional, exempting it (and its neutral backdrop) from the stock-media
 * judgment while leaving every other check — including anything overlaid on top — in force.
 * A talking-head recording also carries whatever was baked into it (a call's name labels and
 * tiles, a produced video's graphics), which no crop can make whole; without saying so, the
 * reviewer reports them as clipped and the agent pushes the crop off the face to hide them.
 */
export function presenterReviewNote(
    sceneGroup: Readonly<SceneGroup> | null | undefined,
): string | null {
    if (isAIAvatarSceneGroup(sceneGroup)) {
        return (
            "The presenter in this scene is the video's AI avatar — a generated on-screen speaker " +
            'against a neutral studio background, on purpose. Do not report the presenter or that ' +
            'background as generic stock or a media-mismatch. Any photo or video laid on top of ' +
            'the presenter is still subject to every check.'
        );
    }
    if (isTalkingHeadSceneGroup(sceneGroup)) {
        return (
            "The person in this scene is the video's talking-head speaker — the user's own " +
            'recording, on purpose. Do not report the speaker or their setting as generic stock ' +
            'or a media-mismatch. The recording may be a video call, a webinar, a screen ' +
            'recording or an already-produced video being reframed, and whatever is burned into ' +
            'the recording — name labels, captions, logos, call tiles, app UI — is footage, not ' +
            "this scene's own content: a crop that cuts through it is a framing choice. Do not " +
            'report it as content-off-frame, text-clipped or content-obscured. Do report a face ' +
            'cut off by the frame edge. Any photo or video laid on top is still subject to every ' +
            'check.'
        );
    }
    return null;
}

/**
 * One line per technique this scene was built with whose normal look resembles an item on the
 * defect list. The reviewer sees only frames, so a strike laid across a word reads to it as text
 * obscured — and the author then edits the assigned technique away to pass.
 */
export function techniqueReviewNotes(techniques: readonly string[]): string[] {
    return techniques.flatMap((skill) => {
        const note = findHtmlDesignSkill(skill)?.reviewNote;
        return note ? [`This scene was built with \`${skill}\`, on purpose. ${note}`] : [];
    });
}

function secondsWithinScene(seconds: number, sceneDurationMs: number): boolean {
    const sceneSeconds = sceneDurationMs / 1000;
    return (
        Number.isFinite(seconds) &&
        sceneSeconds > 0 &&
        seconds >= 0 &&
        seconds <= sceneSeconds + CUE_ROUNDING_TOLERANCE_S
    );
}

export const reviewSceneImagesSchema = z.object({
    scene_ids: z.array(z.string()),
    video_subject: z
        .string()
        .min(1)
        .describe(
            "One line on what the whole video is about. The reviewer judges a scene's footage " +
                "against it when the scene's own text names nothing to show.",
        ),
    seconds: z
        .number()
        .optional()
        .describe('Capture each scene at this time instead of its default frames.'),
});
export type ReviewSceneImagesArgs = z.infer<typeof reviewSceneImagesSchema>;

type ResolvedScene = { sceneId: string; sceneIndex: number };

/**
 * Scenes per reviewer call. The vision call slows with every scene put in it — about 4s for one
 * scene, 5s for three, over 10s past ten, 60s+ at 26 — and every check is judged per scene, so
 * small chunks reviewed side by side finish far sooner than one call over the whole video.
 */
const REVIEW_CHUNK_SIZE = 3;

type ReviewChunk = {
    scenes: readonly ResolvedScene[];
    checks: readonly ReviewCheck[];
    carriesUnattributedPriors: boolean;
};

type ChunkOutcome = { earlyResult: EnhancedToolResult } | { findings: ReviewFinding[] | undefined };

function chunkScenes(scenes: readonly ResolvedScene[]): ResolvedScene[][] {
    const chunks: ResolvedScene[][] = [];
    for (let start = 0; start < scenes.length; start += REVIEW_CHUNK_SIZE) {
        chunks.push(scenes.slice(start, start + REVIEW_CHUNK_SIZE));
    }
    return chunks;
}

/**
 * Renders scenes and hands them to a fresh vision model that judges them against the closed
 * defect list in `review-checks.ts`, reporting through `ReportFindingsTool` rather than prose.
 * A finding blocks (`isSuccess: false`), so one the model would otherwise soften into a suggestion
 * arrives in the same failure shape as a lint error and gets fixed rather than shipped.
 */
export class ReviewSceneImagesTool extends ConversationTool<typeof reviewSceneImagesSchema> {
    public override canRunAsync = true;
    public override runsAfterEdits = true;
    public override readonly childModel = Model.GPT_5_6_LUNA;
    // Everything this call reports blocks, and nothing downstream second-guesses it.
    public override readonly childReasoningEffort = ReasoningEffort.MEDIUM;
    protected override readonly appendsVideoDiff = false;

    protected override readonly schema = reviewSceneImagesSchema;
    protected override readonly toolName = TOOL_NAMES.reviewSceneImages;
    public override readonly subagentAccess = SubagentAccess.UNAVAILABLE;
    protected override readonly toolDescription =
        'Render scenes and check them against a closed list of visual defects. Returns a failure ' +
        'listing every defect found, which you must fix and re-review, or a pass when the scenes ' +
        'are clean.';

    public override getToolDisplayName(): string {
        return 'Reviewing scene';
    }

    public get statusIcon(): string {
        return 'image_search';
    }

    public async execute(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (isReviewSkipped(this.getState())) {
            return { result: reviewSkippedResult(), isSuccess: true };
        }

        if (args.scene_ids.length === 0) {
            return {
                result: 'Error: provide at least one scene id (scene_ids) to review.',
                isSuccess: false,
            };
        }

        const resolvedScenes: ResolvedScene[] = [];
        for (const sceneId of args.scene_ids) {
            const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
            if ('error' in resolved) {
                return { result: `Error: ${resolved.error}`, isSuccess: false };
            }
            resolvedScenes.push({ sceneId, sceneIndex: resolved.sceneIndex });
        }

        // A full scan is the only review that proves a scene clean, so only it records a pass and
        // only it may skip one; a narrowed (`seconds`) review always runs.
        const isFullScan = args.seconds === undefined;
        const toReview: ResolvedScene[] = [];
        let alreadyPassedCount = 0;
        for (const resolved of resolvedScenes) {
            if (
                isFullScan &&
                this.scenePassedUnchanged(context.conversationId, resolved.sceneIndex)
            ) {
                alreadyPassedCount += 1;
            } else {
                toReview.push(resolved);
            }
        }

        if (toReview.length === 0) {
            return { result: allScenesAlreadyPassedResult(alreadyPassedCount), isSuccess: true };
        }

        announceCreationStage(
            this.dispatch,
            this.getState,
            context,
            CreationStage.REVIEWING,
            getVideoSizeAnalyticsProperties(this.luminary),
        );

        const reviewingSceneIds = toReview.map(
            ({ sceneIndex }) => this.luminary.scenes[sceneIndex].id,
        );
        this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: true }));
        // A reviewed scene is considered filled — clear its build-preview blur right away
        // rather than after the vision call returns.
        this.recordSceneFillForScenes(
            toReview.map(({ sceneIndex }) => sceneIndex),
            'review',
        );
        try {
            const result = await this.reviewScenes(args, context, toReview);
            if (result.isSuccess && alreadyPassedCount > 0) {
                return {
                    ...result,
                    result: `${result.result}\n${skippedSceneNote(alreadyPassedCount)}`,
                };
            }
            return result;
        } finally {
            this.dispatch(setSceneReviewing({ sceneIds: reviewingSceneIds, isReviewing: false }));
        }
    }

    private async reviewScenes(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
        resolvedScenes: ResolvedScene[],
    ): Promise<EnhancedToolResult> {
        const hasScan = args.seconds === undefined;

        if (args.seconds !== undefined) {
            const missing = resolvedScenes
                .map(({ sceneId, sceneIndex }) => ({
                    sceneId,
                    scene: this.luminary.scenes[sceneIndex],
                }))
                .filter(({ scene }) => scene && !secondsWithinScene(args.seconds!, scene.duration));
            if (missing.length > 0) {
                const bounds = missing
                    .map(
                        ({ sceneId, scene }) =>
                            `${sceneDisplayId(this.luminary, sceneId)} runs 0–${(scene!.duration / 1000).toFixed(2)}s`,
                    )
                    .join('; ');
                return {
                    result: `Error: ${args.seconds}s is outside the scene(s) asked about — ${bounds}.`,
                    isSuccess: false,
                };
            }
        }

        const uniqueScenes = resolvedScenes.filter(
            (scene, index) =>
                resolvedScenes.findIndex((other) => other.sceneIndex === scene.sceneIndex) ===
                index,
        );

        // An html graphic is judged on staging and phase transitions; a template scene
        // animates in and settles, so those checks would report the norm rather than a defect.
        // The two groups therefore need different check lists, and a list is per conversation —
        // hence separate passes, merged below.
        const groups = [
            {
                isHtmlGraphic: true,
                scenes: uniqueScenes.filter((s) => this.isHtmlGraphic(s)),
            },
            {
                isHtmlGraphic: false,
                scenes: uniqueScenes.filter((s) => !this.isHtmlGraphic(s)),
            },
        ].filter((group) => group.scenes.length > 0);

        const chunks: ReviewChunk[] = groups.flatMap((group) => {
            const checks = applicableChecks({ hasGraphicMotion: hasScan && group.isHtmlGraphic });
            return chunkScenes(group.scenes).map((scenes, chunkIndex) => ({
                scenes,
                checks,
                // Every chunk re-reporting an unattributed prior would list it once per chunk.
                carriesUnattributedPriors: chunkIndex === 0,
            }));
        });

        // Capture stays serial — it renders on the shared live instance — but each chunk's
        // reviewer starts as soon as its frames are in, overlapping the next chunk's capture.
        const pendingOutcomes: Promise<ChunkOutcome>[] = [];
        for (const chunk of chunks) {
            const rendered = await this.renderChunk(args, chunk.scenes);
            if ('earlyResult' in rendered) {
                return rendered.earlyResult;
            }
            pendingOutcomes.push(this.askReviewer(args, context, chunk, rendered.content));
        }
        const outcomes = await Promise.all(pendingOutcomes);

        const findings: ReviewFinding[] = [];
        for (const outcome of outcomes) {
            if ('earlyResult' in outcome) {
                return outcome.earlyResult;
            }
            if (outcome.findings === undefined) {
                // One chunk's reviewer answered in prose instead of reporting. Block on the whole
                // review rather than merging a partial one: the re-review this forces will surface
                // the other chunks' findings again, and a partial pass is how a defect ships.
                return this.buildReviewResult(
                    undefined,
                    resolvedScenes,
                    context.conversationId,
                    hasScan,
                );
            }
            findings.push(...outcome.findings);
        }

        // The reviewer has no warning tier: every finding it reports blocks.
        const checksRun = [...new Set(chunks.flatMap((chunk) => chunk.checks.map((c) => c.id)))];
        analytics.track('Agent: Scene Images Reviewed', {
            ...buildCheckFindingCounts(
                checksRun,
                findings.map((finding) => ({ check: finding.check, severity: 'error' as const })),
                () => ['error'],
            ),
            scene_count: uniqueScenes.length,
        });

        return this.buildReviewResult(findings, resolvedScenes, context.conversationId, hasScan);
    }

    /**
     * What the last review reported and has not seen cleared. Their checks are named in the next
     * instruction: a blind re-review misses a still-present defect roughly a third of the time.
     */
    private priorFindingsFor(
        { scenes, checks, carriesUnattributedPriors }: ReviewChunk,
        conversationId: string | undefined,
    ): ReviewFinding[] {
        const conversationKey = conversationId ?? 'no-conversation';
        const applicable = new Set(checks.map((check) => check.id));
        const keys = [
            // No scene to key on, but it blocked like any other and still needs confirming.
            ...(carriesUnattributedPriors ? [`${conversationKey}:${UNATTRIBUTED_KEY}`] : []),
            ...scenes.map(
                ({ sceneIndex }) => `${conversationKey}:${this.luminary.scenes[sceneIndex].id}`,
            ),
        ];
        return (
            keys
                .flatMap((key) => lastFindingsByScene.get(key) ?? [])
                // A check this review can't judge isn't in its report schema; naming it back
                // would demand an id the schema rejects.
                .filter((finding) => applicable.has(finding.check))
                // The handle the frames are labelled with, or the reviewer reads it as a scene
                // it never saw.
                .map((finding) => ({
                    ...finding,
                    scene_id: this.displaySceneId(finding.scene_id),
                }))
        );
    }

    /** The short handle a scene's frames are labelled with, or the id as given if it resolves to none. */
    private displaySceneId(sceneId: string): string {
        const resolved = resolveSceneById(this.luminary, this.getState(), sceneId);
        return 'error' in resolved ? sceneId : sceneDisplayId(this.luminary, resolved.scene.id);
    }

    /** Whether this scene holds an HtmlAsset — a graphic the agent wrote, not a designer's template. */
    private isHtmlGraphic({ sceneIndex }: ResolvedScene): boolean {
        const scene = this.luminary.scenes[sceneIndex];
        return !!scene && isHtmlGraphicScene(scene);
    }

    /**
     * Renders a chunk's scenes, one after another on the live instance. Returns their frames, or an
     * `earlyResult` the caller should return as-is (a user skip, or a scene that would not render).
     */
    private async renderChunk(
        args: ReviewSceneImagesArgs,
        scenes: readonly ResolvedScene[],
    ): Promise<{ earlyResult: EnhancedToolResult } | { content: (TextContent | ImageContent)[] }> {
        const content: (TextContent | ImageContent)[] = [];
        for (const { sceneId, sceneIndex } of scenes) {
            // Re-checked each time round: rendering a scene is slow, so a skip clicked during a
            // multi-scene review should drop the rest rather than finish the batch and then ask
            // the vision model about a review the user has already waved off.
            if (isReviewSkipped(this.getState())) {
                return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
            }
            const rendered = await this.renderScene(args, sceneId, sceneIndex);
            if ('error' in rendered) {
                return { earlyResult: { result: rendered.error, isSuccess: false } };
            }
            content.push(...rendered.content);
        }
        return { content };
    }

    /**
     * Asks a fresh reviewer about one chunk's rendered frames against that chunk's checks. Returns
     * the findings, `undefined` when the reviewer never reported, or an `earlyResult` the caller
     * should return as-is (a user skip, or a failed vision call).
     */
    private async askReviewer(
        args: ReviewSceneImagesArgs,
        context: ToolExecutionContext,
        chunk: ReviewChunk,
        frames: readonly (TextContent | ImageContent)[],
    ): Promise<ChunkOutcome> {
        const { checks } = chunk;
        const messageContent: (TextContent | ImageContent)[] = [
            ...frames,
            {
                type: 'text',
                text: buildReviewInstruction(
                    checks,
                    this.priorFindingsFor(chunk, context.conversationId),
                    args.video_subject,
                ),
            },
        ];

        const reportFindings = new ReportFindingsTool({
            luminary: this.luminary,
            dispatch: this.dispatch,
            getState: this.getState,
            checks,
        });

        const conversation = this.createChildConversationForToolCall(context.toolCallId, {
            systemMessage: `You are reviewing frames rendered from a video scene.

You did not make this video and have no stake in it. Report what is actually in the frames. Your
judgement is the only check on work whose author cannot see it objectively, so a defect you soften
is a defect that ships.

When describing locations within an image, use terms like: top-left, top-center, top-right,
center-left, center, center-right, bottom-left, bottom-center, bottom-right.`,
            tools: [reportFindings],
            task: 'review_scene_images_tool',
            requiresVideoState: false,
        });

        conversation.addUserMessage(messageContent);

        try {
            const result = await conversation.executeWithLLM({
                parentActionGroup: context.parentActionGroup,
                stopTool: TOOL_NAMES.reportFindings,
            });
            if (!result.success) {
                // A skip cancels the vision call, which surfaces here as a failure.
                if (isReviewSkipped(this.getState())) {
                    return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
                }
                return {
                    earlyResult: {
                        result: `Error during scene review: ${result.message || 'the reviewer did not return a result. Try again in a moment.'}`,
                        isSuccess: false,
                    },
                };
            }
        } catch (error) {
            if (isReviewSkipped(this.getState())) {
                return { earlyResult: { result: reviewSkippedResult(), isSuccess: true } };
            }
            return {
                earlyResult: {
                    result: `Error reviewing scenes: ${error instanceof Error ? error.message : 'Unknown error'}`,
                    isSuccess: false,
                },
            };
        }

        return { findings: reportFindings.captured };
    }

    /**
     * Applies the round cap per scene before formatting the gate. A scene released by the cap is
     * pulled out of the blocking findings and reported as accepted-with-residual instead, so a
     * defect the model can't fix stops blocking without silently vanishing from the result.
     */
    private buildReviewResult(
        captured: ReviewFinding[] | undefined,
        resolvedScenes: readonly ResolvedScene[],
        conversationId: string | undefined,
        /**
         * Only a full scan clears a scene's prior findings. A narrowed review looked at one
         * instant, so silence from it is not evidence a defect is gone — and for a check needing
         * the whole timeline it could not have judged it at all.
         */
        clearsPriors: boolean,
    ): EnhancedToolResult {
        if (captured === undefined) {
            return {
                result:
                    'The reviewer did not report a result. Nothing was checked — these scenes ' +
                    'still need review.',
                isSuccess: false,
            };
        }

        // Keyed on the scene's canonical id (never the raw, agent-echoed sceneId): the vision
        // model is free to answer with any spelling `resolveSceneById` accepts (a shorter or
        // longer prefix, the full uuid, different case), and a duplicate entry in scene_ids must
        // not be counted as two scenes. Dedupe the request the same way.
        const requestedCanonicalIds = new Set(
            resolvedScenes.map(({ sceneIndex }) => this.luminary.scenes[sceneIndex].id),
        );

        const findingsByCanonicalId = new Map<string, ReviewFinding[]>();
        // A finding whose scene_id can't be pinned to one of the requested scenes must never be
        // dropped — the whole reason this tool exists is that a defect silently disappearing is
        // worse than one that keeps blocking. It has no scene identity to key a per-scene round on,
        // so it is tracked per-conversation instead (below) and released by the same cap.
        const unattributedFindings: ReviewFinding[] = [];
        for (const finding of captured) {
            const resolved = resolveSceneById(this.luminary, this.getState(), finding.scene_id);
            if ('error' in resolved || !requestedCanonicalIds.has(resolved.scene.id)) {
                unattributedFindings.push(finding);
                continue;
            }
            const canonicalId = resolved.scene.id;
            const attributed = withFixContext(finding, resolved.scene);
            const existing = findingsByCanonicalId.get(canonicalId);
            if (existing) {
                existing.push(attributed);
            } else {
                findingsByCanonicalId.set(canonicalId, [attributed]);
            }
        }

        const conversationKey = conversationId ?? 'no-conversation';
        const blockingFindings: ReviewFinding[] = [];
        const blockingRepeated: boolean[] = [];
        const releasedNotes: string[] = [];

        // `repeated` (returned for the gate's "unchanged" wording) needs the full, byte-exact
        // signature: that message claims this specific wording came back, so the edit provably
        // did nothing. The round count itself ignores what the scene failed on — see
        // blockingRoundsByScene.
        const trackRound = (runKey: string, findings: readonly ReviewFinding[]) => {
            const signature = findingsSignature(findings);
            const previous = lastFindingsByScene.get(runKey) ?? [];
            const repeated = findingsSignature(previous) === signature;

            lastFindingsByScene.set(runKey, [...findings]);
            const rounds = (blockingRoundsByScene.get(runKey) ?? 0) + 1;
            blockingRoundsByScene.set(runKey, rounds);

            return { rounds, repeated };
        };
        const clearRound = (runKey: string) => {
            // Also called when the cap releases a finding, so an accepted defect stops being
            // named back on every later review.
            lastFindingsByScene.delete(runKey);
            blockingRoundsByScene.delete(runKey);
        };

        if (unattributedFindings.length === 0) {
            if (clearsPriors) {
                clearRound(`${conversationKey}:${UNATTRIBUTED_KEY}`);
            }
        } else {
            const runKey = `${conversationKey}:${UNATTRIBUTED_KEY}`;
            const { rounds, repeated } = trackRound(runKey, unattributedFindings);
            if (rounds > MAX_REVIEW_ROUNDS) {
                const list = unattributedFindings
                    .map(
                        (finding) =>
                            `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}`,
                    )
                    .join('\n');
                releasedNotes.push(
                    `Accepted with the defect(s) noted after ${MAX_REVIEW_ROUNDS} blocking rounds — ` +
                        `reported against a scene id that did not resolve to one of the reviewed ` +
                        `scenes, so it stayed unattributed:\n${list}`,
                );
                clearRound(runKey);
            } else {
                blockingFindings.push(...unattributedFindings);
                blockingRepeated.push(repeated);
            }
        }

        // An unattributed finding is a real, blocking defect the reviewer just mis-addressed — it is
        // usually about one of these very scenes. So while one is outstanding, no requested scene is
        // provably clean: recording a pass here would let that scene skip next round, and if every
        // requested scene skipped the unattributed round would never run again and the defect would
        // vanish without ever reaching the cap's accepted-with-residual note.
        const canRecordPass = clearsPriors && unattributedFindings.length === 0;

        for (const canonicalId of requestedCanonicalIds) {
            const runKey = `${conversationKey}:${canonicalId}`;
            const sceneFindings = findingsByCanonicalId.get(canonicalId);
            if (!sceneFindings || sceneFindings.length === 0) {
                if (clearsPriors) {
                    clearRound(runKey);
                }
                if (canRecordPass) {
                    this.recordScenePassed(runKey, canonicalId);
                }
                continue;
            }

            // It has a defect again, so the pass it may have had no longer holds.
            this.forgetScenePassed(runKey);
            const { rounds, repeated } = trackRound(runKey, sceneFindings);

            if (rounds > MAX_REVIEW_ROUNDS) {
                const list = sceneFindings
                    .map(
                        (finding) =>
                            `- [${finding.check}] scene ${finding.scene_id}: ${finding.detail}`,
                    )
                    .join('\n');
                releasedNotes.push(
                    `Scene ${sceneFindings[0].scene_id}: accepted with the defect(s) noted after ` +
                        `${MAX_REVIEW_ROUNDS} blocking rounds on this scene:\n${list}`,
                );
                clearRound(runKey);
            } else {
                blockingFindings.push(...sceneFindings);
                blockingRepeated.push(repeated);
            }
        }

        if (blockingFindings.length === 0) {
            if (releasedNotes.length === 0) {
                return {
                    result: `Reviewed ${requestedCanonicalIds.size} scene(s): no defects found.`,
                    isSuccess: true,
                };
            }
            return { result: releasedNotes.join('\n'), isSuccess: true };
        }

        // Only "unchanged" when every blocking scene's set is unchanged — a scene with a fresh
        // finding this round means the batch as a whole did move.
        const overallRepeated = blockingRepeated.length > 0 && blockingRepeated.every(Boolean);
        const gate = formatReviewGate(blockingFindings, { repeated: overallRepeated });
        return {
            result: releasedNotes.length > 0 ? `${gate}\n\n${releasedNotes.join('\n')}` : gate,
            isSuccess: false,
        };
    }

    /**
     * The scene's entry in the video state — the same per-scene description get_video_state is built
     * from — serialised as the skip key. Any edit the agent makes to the scene changes it, so a
     * review is skipped only while it is unchanged. Null when it can't be built, so the caller
     * reviews rather than skips.
     */
    private sceneVideoState(scene: Scene): string | null {
        try {
            return JSON.stringify(getSceneDescriptions(scene, this.luminary, this.getState()));
        } catch {
            return null;
        }
    }

    /** Whether this scene passed a full review earlier in the conversation and hasn't changed since. */
    private scenePassedUnchanged(conversationId: string | undefined, sceneIndex: number): boolean {
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return false;
        }
        const key = `${conversationId ?? 'no-conversation'}:${scene.id}`;
        const stored = passedSignatureByScene.get(key);
        if (stored === undefined) {
            return false;
        }
        const current = this.sceneVideoState(scene);
        return current !== null && current === stored;
    }

    private recordScenePassed(runKey: string, canonicalId: string): void {
        const scene = this.luminary.scenes.find((candidate) => candidate.id === canonicalId);
        if (!scene) {
            return;
        }
        const signature = this.sceneVideoState(scene);
        if (signature !== null) {
            passedSignatureByScene.set(runKey, signature);
        }
    }

    private forgetScenePassed(runKey: string): void {
        passedSignatureByScene.delete(runKey);
    }

    private async renderScene(
        args: ReviewSceneImagesArgs,
        sceneId: string,
        sceneIndex: number,
    ): Promise<{ content: (TextContent | ImageContent)[] } | { error: string }> {
        if (sceneIndex < 0 || sceneIndex >= this.luminary.scenes.length) {
            return {
                error: `Error: scene index ${sceneIndex} is out of bounds. Video has ${this.luminary.scenes.length} scenes.`,
            };
        }
        const scene = this.luminary.scenes[sceneIndex];
        if (!scene) {
            return { error: `Error: No scene found at index ${sceneIndex}.` };
        }

        const progressPoints =
            args.seconds !== undefined
                ? [Math.min(1, (args.seconds * 1000) / scene.duration)]
                : reviewProgressPoints(scene, await restPointsOf(scene));
        const frames = await captureSceneFrames(
            this.luminary,
            scene,
            progressPoints,
            SCREENSHOT_MAX_EDGE.high,
        );
        if (!frames.length) {
            return {
                error: `Error: could not render scene ${sceneDisplayId(this.luminary, scene.id)}.`,
            };
        }

        const spoken = (getSceneTranscriptTokens(this.getState(), scene) ?? []).filter(
            (token) => !token.isPause,
        );
        const timed = spoken.length ? timeNarrationWords(scene, spoken) : null;

        const content: (TextContent | ImageContent)[] = [
            {
                type: 'text',
                text:
                    frames.length === 1
                        ? `Scene ${sceneId} — one frame at ${frames[0].label} of the scene's timeline:`
                        : `Scene ${sceneId} — ${frames.length} frames across the scene's timeline:`,
            },
        ];
        const presenterNote = presenterReviewNote(scene.sceneGroup);
        if (presenterNote) {
            content.push({ type: 'text', text: presenterNote });
        }
        for (const note of techniqueReviewNotes(getSceneTechniques(this.getState(), scene.id))) {
            content.push({ type: 'text', text: note });
        }
        // Narration present but its timings can't be mapped onto the scene: attach it once as
        // plain text rather than fabricating a per-frame percentage.
        if (spoken.length && !timed) {
            content.push({
                type: 'text',
                text: formatNarration(spoken.map((token) => token.word).join(' ')),
            });
        }
        frames.forEach((frame, index) => {
            content.push({
                type: 'text',
                text: frameLabel(
                    frame.label,
                    (progressPoints[index] * scene.duration) / 1000,
                    timed,
                ),
            });
            content.push({ type: 'image_url', image_url: { url: frame.dataUrl } });
        });
        return { content };
    }
}

/**
 * The label for a captured frame, folding in the narration spoken at that point of the scene when
 * the scene's word timings are available: "at 4.0s (saying: grew **forty** percent):". Falls back
 * to the bare "at N.Ns:" label when there's no mappable narration. Both are measured from the
 * scene's start, matching the unit the word timings are in.
 */
/**
 * Where the scene's graphic holds still, or null to fall back to the fixed scan. A composition that
 * cannot be measured is still worth reviewing on the scan, so a failed measurement is not the
 * review's failure.
 */
async function restPointsOf(scene: Scene): Promise<number[] | null> {
    const [graphic] = scene.getAssetsByType(VideoData.HTML_ASSET_TYPE);
    if (!graphic) {
        return null;
    }
    try {
        return await getCompositionRestPoints(graphic, scene.duration);
    } catch {
        return null;
    }
}

function frameLabel(label: string, seconds: number, timed: TimedWord[] | null): string {
    if (!timed) {
        return `at ${label}:`;
    }
    const window = narrationWindowAt(timed, seconds);
    if (!window) {
        return `at ${label}:`;
    }
    const verb = window.activeIndex >= 0 ? 'saying' : 'around';
    return `at ${label} (${verb}: ${formatNarrationWindow(window)}):`;
}
